import { useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Copy, Loader2, ExternalLink, Trash2 } from "lucide-react";
import { eur } from "@/lib/format";

const dataIt = (s) => (s ? new Date(s).toLocaleDateString("it-IT") : "—");

// Elenco di sola lettura: incassi creati da «Emetti ricevuta» che doppiano un incasso
// registrato a mano. Niente viene cancellato: la decisione resta allo Studio.
export default function IncassiDoppi() {
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);

  const cerca = async () => {
    setBusy(true); setRes(null);
    try {
      const { data } = await api.get("/admin/incassi-duplicati");
      setRes(data);
      if (data.totale === 0) toast.success("Nessun incasso doppio: i conti delle pratiche sono puliti.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Controllo non riuscito"); }
    finally { setBusy(false); }
  };

  const unisci = async (r) => {
    if (!window.confirm(`Togliere il doppione di ${eur(r.importo)} su ${r.cliente}? `
      + "Resta l'incasso registrato a mano e la ricevuta gli viene collegata.")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/incassi-duplicati/unisci", {
        pratica_id: r.pratica_id, tenere: r.incasso_a_mano.id, rimuovere: r.incasso_da_ricevuta.id,
      });
      toast.success(`Doppione rimosso. Incassato ora ${eur(data.incassato)}`
        + (data.ricevuta_collegata ? ` · ricevuta n. ${data.ricevuta_collegata} collegata` : ""));
      await cerca();
    } catch (e) { toast.error(e?.response?.data?.detail || "Operazione non riuscita"); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-[#1A2B4A]/10 rounded-sm p-4 mb-6" data-testid="incassi-doppi-box">
      <div className="flex items-center gap-2 mb-1">
        <Copy className="w-4 h-4 text-[#C9A84C]" />
        <h3 className="font-serif text-xl text-[#1A2B4A]">Incassi doppi da rivedere</h3>
      </div>
      <p className="text-sm text-[#4a5568] mb-3">
        Cerca due incassi di pari importo sulla stessa pratica: quasi sempre è lo stesso pagamento
        registrato due volte, a mano e dal vecchio «Emetti ricevuta». Il confronto guarda l'istante di
        registrazione, non la data dichiarata, che può essere quella della ricevuta.
        Nessuna riga viene toccata: decidi tu pratica per pratica.
      </p>
      <button data-testid="incassi-doppi-cerca" onClick={cerca} disabled={busy}
        className="inline-flex items-center gap-2 bg-[#1A2B4A] text-white px-4 py-2 rounded-md text-sm font-semibold hover:bg-[#22345c] transition-colors disabled:opacity-60">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Copy className="w-4 h-4" />} Cerca i doppioni
      </button>

      {res && (
        <div className="mt-4" data-testid="incassi-doppi-esito">
          <p className="text-sm text-[#1A2B4A] mb-2">
            {res.totale === 0 ? "Nessun doppione trovato." : `${res.totale} possibile/i doppione/i.`}
          </p>
          {res.righe.map((r) => (
            <div key={r.incasso_da_ricevuta.id} data-testid={`doppione-${r.pratica_id}`}
              className="border-t border-[#1A2B4A]/8 py-2.5 text-[13px]">
              <p className="text-[#1A2B4A] font-medium flex items-center gap-2">
                {r.cliente} · {r.pratica}
                <Link to={`/pratiche/${r.pratica_id}`} className="text-[#C9A84C] hover:underline inline-flex items-center gap-1">
                  <ExternalLink className="w-3.5 h-3.5" /> apri
                </Link>
              </p>
              <p className="text-[12px] text-[#4a5568] leading-relaxed">
                concordato {eur(r.concordato)} · incassato {eur(r.incassato)} · da incassare {eur(r.da_incassare)}<br />
                a mano: {eur(r.importo)} del {dataIt(r.incasso_a_mano.data)}
                {r.incasso_a_mano.registrato ? `, registrato il ${r.incasso_a_mano.registrato}` : ""}
                {r.incasso_a_mano.nota ? ` (${r.incasso_a_mano.nota})` : ""}<br />
                doppione: {eur(r.importo)} del {dataIt(r.incasso_da_ricevuta.data)}
                {r.incasso_da_ricevuta.registrato ? `, registrato il ${r.incasso_da_ricevuta.registrato}` : ""}
                {r.incasso_da_ricevuta.nota ? ` — ${r.incasso_da_ricevuta.nota}` : ""}
              </p>
              <button data-testid={`doppione-unisci-${r.pratica_id}`} onClick={() => unisci(r)} disabled={busy}
                className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-[#9b2c2c]/30 bg-[#fdf6f6]
                           text-[12px] font-semibold text-[#8C2A2A] hover:border-[#9b2c2c]/60 transition-colors disabled:opacity-60">
                <Trash2 className="w-3.5 h-3.5" /> Tieni l'incasso a mano e togli il doppione
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
